import {
  concreteSegmentMatchesParamSlot,
  parseParamSlot,
} from './param-slot-classifier';

export function doesPathMatch(path: string, apiPath: string): boolean {
  if (path === apiPath) return true;

  const normalizedPath = normalizePath(path);
  const normalizedApiPath = normalizePath(apiPath);

  if (normalizedPath === normalizedApiPath) return true;
  if (isPathSuffixMatch(normalizedPath, normalizedApiPath)) return true;
  if (isParameterizedPathMatch(normalizedPath, normalizedApiPath)) return true;

  return false;
}

export function isParameterizedPathMatch(
  path: string,
  apiPath: string,
): boolean {
  const pathSegments = splitPath(path);
  const apiPathSegments = splitPath(apiPath);

  if (pathSegments.length !== apiPathSegments.length) return false;

  for (const [index, pathSegment] of pathSegments.entries()) {
    const apiSegment = apiPathSegments[index];

    if (pathSegment === apiSegment) continue;

    const isPathSegmentParam = isParameterSegment(pathSegment);
    const isApiSegmentParam = isParameterSegment(apiSegment);

    if (isPathSegmentParam && isApiSegmentParam) continue;
    if (isApiSegmentParam !== isPathSegmentParam) return false;

    return false;
  }

  return true;
}

export function isPathSuffixMatch(path: string, apiPath: string): boolean {
  const pathSegments = splitPath(path);
  const apiPathSegments = splitPath(apiPath);

  if (pathSegments.length > apiPathSegments.length) return false;

  const apiPathSuffix = apiPathSegments.slice(-pathSegments.length);

  return isParameterizedPathMatch(
    pathSegments.join('/'),
    apiPathSuffix.join('/'),
  );
}

export function normalizePath(path: string): string {
  const withoutScheme = stripUrlToPath(path.trim());
  const [pathWithoutQueryAndHash] = withoutScheme.split(/[?#]/);

  return pathWithoutQueryAndHash.replace(/^\/+|\/+$/g, '');
}

export function splitPath(path: string): string[] {
  return path.split('/').filter(Boolean);
}

export function pathSegmentCount(path: string): number {
  return splitPath(normalizePath(path)).length;
}

export function colonRouteParamName(segment: string): string | null {
  return segment.startsWith(':') && segment.length > 1
    ? segment.slice(1)
    : null;
}

export function routeParamNamesFromPath(path: string): string[] {
  const withoutScheme = stripUrlToPath(path.trim());
  const [pathname] = withoutScheme.split(/[?#]/);

  return splitPath(pathname).flatMap((segment) => {
    const braceSlot = parseParamSlot(segment);

    if (braceSlot) {
      return [braceSlot.name];
    }

    const colonName = colonRouteParamName(segment);

    return colonName ? [colonName] : [];
  });
}

export function isParameterSegment(segment: string): boolean {
  return (
    parseParamSlot(segment) !== null || colonRouteParamName(segment) !== null
  );
}

export function normalizeEndpointPatternPath(pattern: string): string {
  const trimmed = pattern.trim();

  if (!trimmed) {
    return '/';
  }

  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

  return `/${normalizePath(withLeadingSlash)}`;
}

export function escapeRegexLiteral(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function splitHashRoutedPatternPath({
  path,
  documentPath = '/',
}: {
  path: string;
  documentPath?: string;
}): { documentPath: string; hashRoutePath: string } {
  const normalizedPath = normalizeEndpointPatternPath(path);
  const normalizedDocumentPath = normalizeEndpointPatternPath(documentPath);

  if (normalizedDocumentPath === '/') {
    return { documentPath: '/', hashRoutePath: normalizedPath };
  }

  const documentSegments = splitPath(normalizedDocumentPath);
  const pathSegments = splitPath(normalizedPath);

  if (pathSegments.length < documentSegments.length) {
    return { documentPath: '/', hashRoutePath: normalizedPath };
  }

  const hashSegments = pathSegments.slice(documentSegments.length);

  return {
    documentPath: normalizedDocumentPath,
    hashRoutePath:
      hashSegments.length === 0
        ? '/'
        : normalizeEndpointPatternPath(`/${hashSegments.join('/')}`),
  };
}

export function buildWebpageRegexFromPatternPath({
  origin,
  patternPath,
  hashRouting = false,
  hashbang = false,
  documentPath = '/',
}: {
  origin: string;
  patternPath: string;
  hashRouting?: boolean;
  hashbang?: boolean;
  documentPath?: string;
}): string {
  const escapedOrigin = escapeRegexLiteral(origin);
  const normalizedPatternPath = normalizeEndpointPatternPath(patternPath);

  if (hashRouting) {
    const { documentPath: documentBase, hashRoutePath } =
      splitHashRoutedPatternPath({
        path: normalizedPatternPath,
        documentPath,
      });
    const escapedDocumentPath =
      documentBase === '/'
        ? '/?'
        : `${documentBase
            .split('/')
            .filter((segment) => segment.length > 0)
            .map((segment) => patternSegmentToRegexSource(segment))
            .reduce((path, segment) => `${path}/${segment}`, '')}/?`;
    const hashPrefix = hashbang ? '#!' : '#';
    const hashSegments = normalizeEndpointPatternPath(hashRoutePath)
      .split('/')
      .map((segment) => patternSegmentToRegexSource(segment))
      .filter(Boolean);

    if (hashSegments.length === 0) {
      const emptyHash = hashbang ? '(?:#!/?)?' : '(?:#/?)?';

      return `^${escapedOrigin}${escapedDocumentPath}${emptyHash}(?:\\?.*)?$`;
    }

    return `^${escapedOrigin}${escapedDocumentPath}${hashPrefix}/${hashSegments.join('/')}.*$`;
  }

  const escapedSegments = normalizedPatternPath
    .split('/')
    .map((segment) => patternSegmentToRegexSource(segment));

  return `^${escapedOrigin}${escapedSegments.join('/')}.*$`;
}

export function createPageUrlWebpageRegexMatcher(
  regex: string,
): (pageUrl: string) => boolean {
  let pattern: RegExp;

  try {
    pattern = new RegExp(regex, 'i');
  } catch {
    return () => false;
  }

  const matchesPathname = regex.startsWith('^/');

  return (pageUrl) => {
    if (pattern.test(pageUrl)) {
      return true;
    }

    if (!matchesPathname) {
      return false;
    }

    try {
      return pattern.test(new URL(pageUrl).pathname);
    } catch {
      return false;
    }
  };
}

export function doesPageUrlMatchWebpageRegex({
  pageUrl,
  regex,
}: {
  pageUrl: string;
  regex: string;
}): boolean {
  return createPageUrlWebpageRegexMatcher(regex)(pageUrl);
}

function patternSegmentToRegexSource(segment: string): string {
  if (!segment) {
    return '';
  }

  if (isParameterSegment(segment)) {
    return '[^/]+';
  }

  const embeddedParts = segment.split(/(\{[a-z][a-z0-9_]*\})/i);

  if (embeddedParts.length === 1) {
    return escapeRegexLiteral(segment);
  }

  return embeddedParts
    .map((part) => {
      if (parseParamSlot(part)?.name === 'locale') {
        return escapeRegexLiteral(part);
      }

      if (/^\{[a-z][a-z0-9_]*\}$/i.test(part)) {
        return '[^/]+';
      }

      return escapeRegexLiteral(part);
    })
    .join('');
}

function segmentMatchesPatternSegment({
  concreteSegment,
  patternSegment,
}: {
  concreteSegment: string;
  patternSegment: string;
}): boolean {
  const typedMatch = concreteSegmentMatchesParamSlot({
    concreteSegment,
    patternSegment,
  });

  if (typedMatch !== null) {
    return typedMatch;
  }

  if (isParameterSegment(patternSegment)) {
    return true;
  }

  const embeddedParts = patternSegment.split(/(\{[a-z][a-z0-9_]*\})/i);

  if (embeddedParts.length === 1) {
    return concreteSegment === patternSegment;
  }

  if (embeddedParts.some((part) => parseParamSlot(part)?.name === 'locale')) {
    return false;
  }

  const regexSource = embeddedParts
    .map((part) => {
      if (/^\{[a-z][a-z0-9_]*\}$/i.test(part)) {
        return '.+';
      }

      return escapeRegexLiteral(part);
    })
    .join('');

  return new RegExp(`^${regexSource}$`).test(concreteSegment);
}

export function doesConcretePathMatchPattern(
  concretePath: string,
  pattern: string,
): boolean {
  const concreteSegments = splitPath(concretePath);
  const patternSegments = splitPath(pattern);

  if (concreteSegments.length !== patternSegments.length) return false;

  return patternSegments.every((patternSeg, index) =>
    segmentMatchesPatternSegment({
      concreteSegment: concreteSegments[index] ?? '',
      patternSegment: patternSeg,
    }),
  );
}

function countPatternParamPlaceholders(pattern: string): number {
  return pattern.match(/\{[^}]+\}/g)?.length ?? 0;
}

export function findMostSpecificPatternMatch<T>({
  concretePath,
  candidates,
  patternOf,
}: {
  concretePath: string;
  candidates: readonly T[];
  patternOf: (candidate: T) => string;
}): T | undefined {
  let best: T | undefined;
  let bestParamCount = Number.POSITIVE_INFINITY;

  for (const candidate of candidates) {
    const pattern = patternOf(candidate);

    if (!doesConcretePathMatchPattern(concretePath, pattern)) continue;
    const paramCount = countPatternParamPlaceholders(pattern);

    if (paramCount >= bestParamCount) continue;
    best = candidate;
    bestParamCount = paramCount;
    if (paramCount === 0) break;
  }

  return best;
}

function stripUrlToPath(input: string): string {
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(input)) return input;

  try {
    return new URL(input).pathname;
  } catch {
    return input;
  }
}
